import { useState, useEffect } from 'react';
import { MantineProvider, Button, Text, Container, Stack } from '@mantine/core';

const API_URL = 'https://clicker-worker.zimennik.workers.dev';

// Получаем или создаем ID пользователя
const getUserId = async (): Promise<string> => {
  // Проверяем localStorage
  const savedUserId = localStorage.getItem('userId');
  if (savedUserId) {
    return savedUserId;
  }

  // Создаем нового пользователя
  try {
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: `user_${Math.random().toString(36).substring(2, 9)}`
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.details || 'Failed to create user');
    }

    // Сохраняем ID в localStorage
    localStorage.setItem('userId', data.id);
    return data.id;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

function App() {
  const [counter, setCounter] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Инициализация пользователя и получение начального значения счетчика
  useEffect(() => {
    const init = async () => {
      try {
        // Получаем ID пользователя
        const id = await getUserId();
        setUserId(id);

        // Получаем значение счетчика
        const response = await fetch(`${API_URL}/api/counter`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.details || 'Failed to fetch counter');
        }
        setCounter(data.value);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize');
        console.error('Initialization error:', err);
      }
    };

    init();
    // Обновляем счетчик каждые 2 секунды
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/counter`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.details || 'Failed to fetch counter');
        }
        setCounter(data.value);
      } catch (err) {
        console.error('Error fetching counter:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Отправляем клик
  const handleClick = async () => {
    if (!userId) {
      setError('Waiting for initialization...');
      return;
    }

    try {
      setError(null);
      // Оптимистично обновляем счетчик
      setCounter(prev => prev + 1);

      const response = await fetch(`${API_URL}/api/clicks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clicks: 1,
          timestamp: Date.now(),
          userId: userId
        })
      });

      const data = await response.json();
      if (!response.ok) {
        // Если ошибка - откатываем изменение
        setCounter(prev => prev - 1);
        throw new Error(data.details || 'Failed to send click');
      }
    } catch (err) {
      // Если ошибка - откатываем изменение и показываем ошибку
      setCounter(prev => prev - 1);
      setError(err instanceof Error ? err.message : 'Failed to send click');
      console.error('Error sending click:', err);
    }
  };

  return (
    <MantineProvider>
      <Container 
        size="sm" 
        style={{ 
          height: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Stack align="center" gap="xl">
          <Button
            color="red"
            size="xl"
            radius="xl"
            onClick={handleClick}
            disabled={!userId}
            style={{
              width: '200px',
              height: '200px',
              fontSize: '24px',
              fontWeight: 'bold'
            }}
          >
            CLICK ME!
          </Button>
          <Stack gap="xs" align="center">
            <Text size="xl" fw={700}>
              Счетчик: {counter.toLocaleString()}
            </Text>
            {error && (
              <Text size="sm" c="red">
                {error}
              </Text>
            )}
          </Stack>
        </Stack>
      </Container>
    </MantineProvider>
  );
}

export default App;
